import { z } from "zod";
import { MAX_MESSAGE_LENGTH } from "./rules.js";
/** Reactions offered in the quick bar (any emoji is accepted). */
export const QUICK_REACTIONS = ["❤️", "😂", "👍", "😮", "😢", "🙏"];
// ---------- WebSocket protocol ----------
export const wsClientMessage = z.discriminatedUnion("type", [
    z.object({ type: z.literal("auth"), token: z.string() }),
    z.object({
        type: z.literal("send"),
        clientMsgId: z.uuid(),
        conversationId: z.uuid(),
        kind: z.enum(["text", "photo"]).default("text"),
        body: z.string().max(MAX_MESSAGE_LENGTH).default(""),
        attachmentId: z.uuid().optional(),
        replyToId: z.uuid().optional(),
    }),
    z.object({ type: z.literal("typing"), conversationId: z.uuid() }),
    z.object({ type: z.literal("read"), conversationId: z.uuid(), seq: z.number().int().nonnegative() }),
    z.object({ type: z.literal("delivered"), conversationId: z.uuid(), seq: z.number().int().nonnegative() }),
    z.object({ type: z.literal("viewing"), conversationId: z.uuid().nullable() }),
    z.object({ type: z.literal("ping") }),
]);
// ---------- REST bodies ----------
export const markReadBody = z.object({ seq: z.number().int().nonnegative() });
export const inviteBody = z.object({ userIds: z.array(z.uuid()).min(1).max(100) });
export const visibilityBody = z.object({ visibility: z.enum(["open", "closed"]) });
export const muteBody = z.object({ until: z.iso.datetime().nullable() });
export const reactionBody = z.object({ emoji: z.string().min(1).max(16) });
export const dmBody = z.object({ userId: z.uuid() });
/** System message meta → display text. Meta carries names so old messages stay readable. */
export function systemMessageText(meta) {
    const m = (meta ?? {});
    const who = m.firstName ?? "Someone";
    switch (m.type) {
        case "member_joined":
            return `${who} joined the village 👋`;
        case "circle_created":
            return `${who} started ${m.title ?? "this circle"}`;
        case "members_invited":
            return `${who} added ${listNames(m.names ?? [])}`;
        case "member_joined_circle":
            return `${who} joined`;
        case "member_left":
            return `${who} left`;
        case "visibility_changed":
            return `${who} made this circle ${m.visibility === "open" ? "open — anyone can find and join it" : "closed — invite only"}`;
        case "coop_matched":
            return `🤝 ${who} accepted ${m.sitterFirstName ?? "a"}'s offer to sit: ${m.title ?? "a sit"}${m.when ? `, ${m.when}` : ""}. Use this chat to plan the details.`;
        default:
            return "";
    }
}
function listNames(n) {
    if (n.length <= 1)
        return n[0] ?? "someone";
    if (n.length === 2)
        return `${n[0]} and ${n[1]}`;
    return `${n.slice(0, -1).join(", ")} and ${n[n.length - 1]}`;
}
/** Preview text for an event shared into a chat. */
export function eventShareText(meta) {
    const title = meta?.title;
    return title ? `📅 ${title}` : "📅 Shared a meetup";
}
/**
 * "Who's here" line for a conversation card: names the active moms instead of only counting them.
 * `activeNow` is the true count; `preview` lists active moms first (with `active` set), so names come from it.
 *   1 → "Katie is here now" · 2 → "Katie & Bettina are here now" · 4 → "Katie, Bettina +2 here now"
 */
export function presenceLabel(activeNow, preview) {
    if (activeNow <= 0)
        return null;
    const names = preview.filter((p) => p.active && p.firstName).map((p) => p.firstName);
    if (names.length === 0)
        return `${activeNow} ${activeNow === 1 ? "mom is" : "moms are"} here now`;
    if (activeNow === 1)
        return `${names[0]} is here now`;
    if (activeNow === 2 && names.length >= 2)
        return `${names[0]} & ${names[1]} are here now`;
    const shown = names.slice(0, 2);
    return `${shown.join(", ")} +${activeNow - shown.length} here now`;
}
//# sourceMappingURL=chat.js.map